import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { toast } from "react-toastify";

import { httpClient } from "../network";
import {
  addMarketingListClientsEndpoint,
  addMarketingListContactEndpoint,
  bulkRemoveMarketingListMembersEndpoint,
  commitMarketingCsvEndpoint,
  createMarketingListEndpoint,
  deleteMarketingListEndpoint,
  getMarketingAvailableClientsEndpoint,
  getMarketingListEndpoint,
  getMarketingListMembersEndpoint,
  getMarketingListRecipientsEndpoint,
  getMarketingListsEndpoint,
  marketingCsvTemplateEndpoint,
  marketingImportHistoryEndpoint,
  removeMarketingListMemberEndpoint,
  updateMarketingListEndpoint,
  validateMarketingCsvEndpoint,
} from "../endpoints/marketingEndpoints";
import {
  CsvAnalysis,
  CsvImportResult,
  MarketingImportLog,
  MarketingList,
  MarketingListDetail,
  MarketingListMember,
  MarketingPickerClient,
  PaginatedMarketingResponse,
} from "@/types/marketing";

const LISTS_KEY = "marketingLists";
const LIST_KEY = "marketingList";
const MEMBERS_KEY = "marketingListMembers";
const PICKER_KEY = "marketingAvailableClients";

/** Backend messages are already user-facing Bosnian, so surface them as-is. */
const errorMessage = (error: any, fallback: string): string => {
  const message = error?.response?.data?.message;
  if (typeof message === "string") return message;
  if (Array.isArray(message) && message[0]?.error) return message[0].error;
  return fallback;
};

export interface MarketingListsQuery {
  search?: string;
  page?: number;
  pageSize?: number;
}

export const useGetMarketingLists = (
  params: MarketingListsQuery
): UseQueryResult<PaginatedMarketingResponse<MarketingList>, unknown> =>
  useQuery({
    queryKey: [LISTS_KEY, params],
    queryFn: async () => {
      const response: AxiosResponse<PaginatedMarketingResponse<MarketingList>> =
        await httpClient.get(getMarketingListsEndpoint(), { params });
      return response.data;
    },
    placeholderData: keepPreviousData,
  });

export const useGetMarketingList = (
  id: string
): UseQueryResult<MarketingListDetail, unknown> =>
  useQuery({
    queryKey: [LIST_KEY, id],
    enabled: !!id,
    queryFn: async () => {
      const response: AxiosResponse<{ data: MarketingListDetail }> =
        await httpClient.get(getMarketingListEndpoint(id));
      return response.data.data;
    },
  });

export const useCreateMarketingList = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; description?: string }) => {
      const response = await httpClient.post(createMarketingListEndpoint(), payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LISTS_KEY] });
      toast.success("Lista je kreirana");
    },
    onError: (error) => {
      toast.error(errorMessage(error, "Greška prilikom kreiranja liste"));
    },
  });
};

export const useUpdateMarketingList = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: {
      id: string;
      name?: string;
      description?: string;
    }) => {
      const response = await httpClient.patch(updateMarketingListEndpoint(id), payload);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [LISTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [LIST_KEY, variables.id] });
      toast.success("Lista je ažurirana");
    },
    onError: (error) => {
      toast.error(errorMessage(error, "Greška prilikom ažuriranja liste"));
    },
  });
};

export const useDeleteMarketingList = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await httpClient.delete(deleteMarketingListEndpoint(id));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LISTS_KEY] });
      toast.success("Lista je obrisana. Klijenti su ostali u bazi klijenata.");
    },
    onError: (error) => {
      toast.error(errorMessage(error, "Greška prilikom brisanja liste"));
    },
  });
};

export interface MarketingMembersQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  type?: "CLIENT" | "CONTACT";
  consent?: "CONSENTED" | "NOT_CONSENTED" | "UNKNOWN";
}

export const useGetMarketingListMembers = (
  id: string,
  params: MarketingMembersQuery
): UseQueryResult<PaginatedMarketingResponse<MarketingListMember>, unknown> =>
  useQuery({
    queryKey: [MEMBERS_KEY, id, params],
    enabled: !!id,
    queryFn: async () => {
      const response: AxiosResponse<
        PaginatedMarketingResponse<MarketingListMember>
      > = await httpClient.get(getMarketingListMembersEndpoint(id), { params });
      return response.data;
    },
    placeholderData: keepPreviousData,
  });

export const useGetMarketingAvailableClients = (
  id: string,
  params: { search?: string; page?: number; pageSize?: number },
  enabled: boolean
): UseQueryResult<PaginatedMarketingResponse<MarketingPickerClient>, unknown> =>
  useQuery({
    queryKey: [PICKER_KEY, id, params],
    enabled: enabled && !!id,
    queryFn: async () => {
      const response: AxiosResponse<
        PaginatedMarketingResponse<MarketingPickerClient>
      > = await httpClient.get(getMarketingAvailableClientsEndpoint(id), {
        params,
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
  });

const invalidateListData = (queryClient: ReturnType<typeof useQueryClient>, id: string) => {
  queryClient.invalidateQueries({ queryKey: [MEMBERS_KEY, id] });
  queryClient.invalidateQueries({ queryKey: [LIST_KEY, id] });
  queryClient.invalidateQueries({ queryKey: [PICKER_KEY, id] });
  queryClient.invalidateQueries({ queryKey: [LISTS_KEY] });
};

export const useAddClientsToMarketingList = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: {
      id: string;
      clientIds?: string[];
      addAllMatching?: boolean;
      search?: string;
    }) => {
      const response = await httpClient.post(addMarketingListClientsEndpoint(id), payload);
      return response.data;
    },
    onSuccess: (data, variables) => {
      invalidateListData(queryClient, variables.id);
      toast.success(data?.message ?? "Klijenti su dodani na listu");
    },
    onError: (error) => {
      toast.error(errorMessage(error, "Greška prilikom dodavanja klijenata"));
    },
  });
};

export const useAddContactToMarketingList = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: {
      id: string;
      firstName?: string;
      lastName?: string;
      phoneNumber?: string;
      email?: string;
      marketingConsent?: boolean;
    }) => {
      const response = await httpClient.post(addMarketingListContactEndpoint(id), payload);
      return response.data;
    },
    onSuccess: (data, variables) => {
      invalidateListData(queryClient, variables.id);
      toast.success(data?.message ?? "Kontakt je dodan na listu");
    },
    onError: (error) => {
      toast.error(errorMessage(error, "Greška prilikom dodavanja kontakta"));
    },
  });
};

export const useRemoveMarketingListMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, memberId }: { id: string; memberId: string }) => {
      const response = await httpClient.delete(
        removeMarketingListMemberEndpoint(id, memberId)
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      invalidateListData(queryClient, variables.id);
      toast.success("Kontakt je uklonjen sa liste");
    },
    onError: (error) => {
      toast.error(errorMessage(error, "Greška prilikom uklanjanja kontakta"));
    },
  });
};

export const useBulkRemoveMarketingListMembers = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, memberIds }: { id: string; memberIds: string[] }) => {
      const response = await httpClient.post(
        bulkRemoveMarketingListMembersEndpoint(id),
        { memberIds }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      invalidateListData(queryClient, variables.id);
      toast.success(data?.message ?? "Kontakti su uklonjeni sa liste");
    },
    onError: (error) => {
      toast.error(errorMessage(error, "Greška prilikom uklanjanja kontakata"));
    },
  });
};

/** Step 2/3 of the import: dry run, nothing is written. */
export const useValidateMarketingCsv = () =>
  useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const formData = new FormData();
      formData.append("file", file);
      const response: AxiosResponse<{ data: CsvAnalysis }> = await httpClient.post(
        validateMarketingCsvEndpoint(id),
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data.data;
    },
    onError: (error) => {
      toast.error(errorMessage(error, "CSV fajl nije moguće pročitati"));
    },
  });

/** Step 4 of the import: runs only after the admin confirms the preview. */
export const useCommitMarketingCsv = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("consentConfirmed", "true");
      const response: AxiosResponse<{ data: CsvImportResult }> = await httpClient.post(
        commitMarketingCsvEndpoint(id),
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      invalidateListData(queryClient, variables.id);
    },
    onError: (error) => {
      toast.error(errorMessage(error, "Import nije uspio"));
    },
  });
};

export interface MarketingRecipientsPreview {
  totalMembers: number;
  eligible: number;
  excludedNoConsent: number;
  excludedNoPhone: number;
  recipients: { phoneNumber: string; clientId?: string; firstName: string | null; lastName: string | null }[];
}

/** Powers the campaign screen's "who will actually receive this" preview. */
export const useGetMarketingListRecipients = (
  id: string,
  enabled: boolean
): UseQueryResult<MarketingRecipientsPreview, unknown> =>
  useQuery({
    queryKey: ["marketingListRecipients", id],
    enabled: enabled && !!id,
    queryFn: async () => {
      const response: AxiosResponse<{ data: MarketingRecipientsPreview }> =
        await httpClient.get(getMarketingListRecipientsEndpoint(id));
      return response.data.data;
    },
  });

export const useGetMarketingImportHistory = (
  id: string,
  enabled: boolean
): UseQueryResult<MarketingImportLog[], unknown> =>
  useQuery({
    queryKey: ["marketingImportHistory", id],
    enabled: enabled && !!id,
    queryFn: async () => {
      const response: AxiosResponse<{ data: MarketingImportLog[] }> =
        await httpClient.get(marketingImportHistoryEndpoint(id));
      return response.data.data;
    },
  });

/**
 * The template is served with an auth cookie + x-schema header, so it has to
 * go through httpClient rather than a plain <a download>.
 */
export const downloadMarketingCsvTemplate = async () => {
  try {
    const response = await httpClient.get(marketingCsvTemplateEndpoint(), {
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "rezervator-marketing-kontakti-primjer.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch {
    toast.error("Preuzimanje primjera CSV fajla nije uspjelo");
  }
};
